/**
 * AISettingsScreen
 * Configure AI-powered encouragement messages
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  ScrollView,
  TextInput,
  Switch,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { AISettingsScreenProps } from '../navigation/types';
import { storage } from '../services/storage/StorageAdapter';

const AI_SETTINGS_KEY = '@ai_settings';

interface AISettings {
  enabled: boolean;
  apiKey: string;
  useTemplates: boolean; // Fallback to templates if AI fails
}

export const AISettingsScreen: React.FC<AISettingsScreenProps> = ({ navigation }) => {
  const [settings, setSettings] = useState<AISettings>({
    enabled: false,
    apiKey: '',
    useTemplates: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const stored = await storage.getString(AI_SETTINGS_KEY);
      if (stored) {
        setSettings(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async (newSettings: AISettings) => {
    try {
      setIsSaving(true);
      await storage.setString(AI_SETTINGS_KEY, JSON.stringify(newSettings));
      setSettings(newSettings);
      Alert.alert('Settings Saved', 'Your AI encouragement settings have been updated.');
    } catch (error) {
      Alert.alert('Error', 'Failed to save settings');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleEnabled = (value: boolean) => {
    if (value && !settings.apiKey) {
      Alert.alert(
        'API Key Required',
        'Please enter your Claude API key to enable AI-generated messages.',
        [{ text: 'OK' }]
      );
      return;
    }
    saveSettings({ ...settings, enabled: value });
  };

  const handleSaveApiKey = () => {
    if (!settings.apiKey.trim()) {
      Alert.alert('Error', 'Please enter a valid API key');
      return;
    }
    saveSettings(settings);
  };

  const handleTestGeneration = () => {
    Alert.alert(
      'AI Message Generation',
      'This feature will use Claude API to analyze your completion data and generate personalized encouragement messages.\n\nThe AI will:\n• Read your completion history\n• Detect patterns (streaks, comebacks, consistency)\n• Generate contextual messages\n\nNote: This requires an active Anthropic API key.',
      [
        {
          text: 'Learn More',
          onPress: () => {
            Alert.alert(
              'How It Works',
              'When you complete a goal, the app exports your recent completion data to CSV format and sends it to Claude API with a prompt asking for an encouraging message. The AI analyzes your patterns and generates a personalized message.\n\nYour data is only sent when generating messages and is not stored by Anthropic beyond the API call.'
            );
          },
        },
        { text: 'Close' },
      ]
    );
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>AI Encouragement</Text>
            <Text style={styles.subtitle}>
              Get personalized encouragement messages powered by Claude AI
            </Text>
          </View>

          {/* Enable AI Section */}
          <View style={styles.section}>
            <View style={styles.settingRow}>
              <View style={styles.settingInfo}>
                <Text style={styles.settingTitle}>Enable AI Messages</Text>
                <Text style={styles.settingDescription}>
                  Use Claude AI to generate personalized encouragement based on your progress
                </Text>
              </View>
              <Switch
                value={settings.enabled}
                onValueChange={handleToggleEnabled}
                disabled={isSaving}
              />
            </View>
          </View>

          {/* API Key Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Claude API Configuration</Text>
            <Text style={styles.sectionDescription}>
              Enter your Anthropic API key to enable AI-generated messages
            </Text>

            <View style={styles.apiKeyContainer}>
              <TextInput
                style={styles.apiKeyInput}
                value={settings.apiKey}
                onChangeText={(text) => setSettings({ ...settings, apiKey: text })}
                placeholder="sk-ant-..."
                secureTextEntry={!showApiKey}
                autoCapitalize="none"
                autoCorrect={false}
                editable={!isSaving}
              />
              <TouchableOpacity
                style={styles.toggleVisibilityButton}
                onPress={() => setShowApiKey(!showApiKey)}
              >
                <Text style={styles.toggleVisibilityText}>{showApiKey ? 'Hide' : 'Show'}</Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
              onPress={handleSaveApiKey}
              disabled={isSaving}
            >
              {isSaving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.saveButtonText}>Save API Key</Text>
              )}
            </TouchableOpacity>

            <View style={styles.helpSection}>
              <Text style={styles.helpTitle}>How to get an API key:</Text>
              <Text style={styles.helpText}>
                1. Visit console.anthropic.com{'\n'}
                2. Sign up or log in{'\n'}
                3. Go to API Keys section{'\n'}
                4. Create a new API key{'\n'}
                5. Copy and paste it here
              </Text>
            </View>
          </View>

          {/* Options Section */}
          <View style={styles.section}>
            <View style={styles.settingRow}>
              <View style={styles.settingInfo}>
                <Text style={styles.settingTitle}>Use Template Fallback</Text>
                <Text style={styles.settingDescription}>
                  Fall back to template messages if AI generation fails
                </Text>
              </View>
              <Switch
                value={settings.useTemplates}
                onValueChange={(value) => setSettings({ ...settings, useTemplates: value })}
                disabled={isSaving}
              />
            </View>
          </View>

          {/* Test Section */}
          <View style={styles.section}>
            <TouchableOpacity
              style={styles.testButton}
              onPress={handleTestGeneration}
            >
              <Text style={styles.testButtonText}>How AI Generation Works</Text>
            </TouchableOpacity>
          </View>

          {/* Privacy Notice */}
          <View style={styles.privacySection}>
            <Text style={styles.privacyTitle}>Privacy & Data Usage</Text>
            <Text style={styles.privacyText}>
              When AI encouragement is enabled, your completion data is sent to Anthropic's API
              to generate personalized messages. The data includes:
              {'\n\n'}• Recent completion history (last 30-90 days){'\n'}
              • Goal names and types{'\n'}
              • Streak and completion statistics{'\n\n'}
              Your API key is stored locally on your device and never sent to our servers.
              Anthropic does not store your data beyond the API call duration.
            </Text>
          </View>

          {/* Back Button */}
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.backButtonText}>Back to Settings</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    padding: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    marginBottom: 32,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: '#333',
    marginBottom: 12,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
    lineHeight: 20,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  settingInfo: {
    flex: 1,
    marginRight: 16,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  settingDescription: {
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
  },
  apiKeyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  apiKeyInput: {
    flex: 1,
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  toggleVisibilityButton: {
    marginLeft: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  toggleVisibilityText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '600',
  },
  saveButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 16,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  helpSection: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 12,
  },
  helpTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  helpText: {
    fontSize: 13,
    color: '#666',
    lineHeight: 20,
  },
  testButton: {
    backgroundColor: '#f5f5f5',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
  },
  testButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
  privacySection: {
    backgroundColor: '#FFF9E6',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#FFE4A3',
  },
  privacyTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#B85C00',
    marginBottom: 8,
  },
  privacyText: {
    fontSize: 13,
    color: '#8B4513',
    lineHeight: 20,
  },
  backButton: {
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#ddd',
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
});
